// components/CreateProductModal.jsx
import React, { useState } from "react";
import { X } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreateProductModal = ({ onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    productName: "",
    productCode: "",
    productFamily: "",
    active: true,
    productDescription: "",
    listPrice: "",
    costPrice: "",
    quantityUnitOfMeasure: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const submitData = {
        ...formData,
        listPrice: formData.listPrice ? parseFloat(formData.listPrice) : null,
        costPrice: formData.costPrice ? parseFloat(formData.costPrice) : null,
      };

      await apiService.createProduct(submitData);
      onSuccess();
    } catch (err) {
      console.error("Error creating product:", err);
      setError(err.response?.data?.message || "Failed to create product");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndNew = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const submitData = {
        ...formData,
        listPrice: formData.listPrice ? parseFloat(formData.listPrice) : null,
        costPrice: formData.costPrice ? parseFloat(formData.costPrice) : null,
      };

      await apiService.createProduct(submitData);

      // Reset form for new entry
      setFormData({
        productName: "",
        productCode: "",
        productFamily: "",
        active: true,
        productDescription: "",
        listPrice: "",
        costPrice: "",
        quantityUnitOfMeasure: "",
      });

      setError("");
    } catch (err) {
      console.error("Error creating product:", err);
      setError(err.response?.data?.message || "Failed to create product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto border ${
          isDarkMode
            ? "bg-[#13161B] border-gray-700"
            : "bg-white border-gray-300"
        }`}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2
              className={`text-xl font-bold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              New Product
            </h2>
            <button
              onClick={onClose}
              className={`transition-colors ${
                isDarkMode
                  ? "text-gray-400 hover:text-white"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <div
              className={`mb-4 p-3 rounded-lg text-sm ${
                isDarkMode
                  ? "bg-red-500/10 border border-red-500/50 text-red-400"
                  : "bg-red-50 border border-red-200 text-red-600"
              }`}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div
              className={`border-b pb-4 ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Product Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <span className="text-red-500">*</span> Product Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.productName}
                    onChange={(e) =>
                      setFormData({ ...formData, productName: e.target.value })
                    }
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="Enter product name"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Product Code
                  </label>
                  <input
                    type="text"
                    value={formData.productCode}
                    onChange={(e) =>
                      setFormData({ ...formData, productCode: e.target.value })
                    }
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="Auto-generated if left blank"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Product Family
                  </label>
                  <input
                    type="text"
                    value={formData.productFamily}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        productFamily: e.target.value,
                      })
                    }
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="e.g., Hardware, Software, Services"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Active
                  </label>
                  <div className="flex items-center h-[42px]">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) =>
                        setFormData({ ...formData, active: e.target.checked })
                      }
                      className={`w-5 h-5 border rounded text-blue-500 focus:ring-2 focus:ring-blue-500 ${
                        isDarkMode
                          ? "bg-[#0C0E12] border-gray-700"
                          : "bg-white border-gray-300"
                      }`}
                    />
                    <span
                      className={`ml-2 text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Product is active
                    </span>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Product Description
                  </label>
                  <textarea
                    rows="4"
                    value={formData.productDescription}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        productDescription: e.target.value,
                      })
                    }
                    className={`w-full px-4 py-2 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="Enter product description"
                  />
                </div>
              </div>
            </div>

            <div
              className={`border-b pb-4 ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Pricing Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    List Price (£)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.listPrice}
                    onChange={(e) =>
                      setFormData({ ...formData, listPrice: e.target.value })
                    }
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Cost Price (£)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.costPrice}
                    onChange={(e) =>
                      setFormData({ ...formData, costPrice: e.target.value })
                    }
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div>
              <h3
                className={`text-lg font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Additional Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Quantity Unit of Measure
                  </label>
                  <input
                    type="text"
                    value={formData.quantityUnitOfMeasure}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        quantityUnitOfMeasure: e.target.value,
                      })
                    }
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    placeholder="e.g., Each, License, Subscription"
                  />
                </div>
              </div>
            </div>

            <div
              className={`flex justify-end gap-3 pt-4 border-t ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <button
                type="button"
                onClick={onClose}
                className={`px-6 py-2 border rounded-lg transition-colors ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700"
                    : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveAndNew}
                disabled={loading}
                className={`px-6 py-2 border rounded-lg transition-colors disabled:opacity-50 ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700"
                    : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                {loading ? "Saving..." : "Save & New"}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 rounded-lg text-white font-medium transition-all disabled:opacity-50"
                style={{
                  background:
                    "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                }}
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </form>

          <div
            className={`mt-6 pt-4 border-t ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <p
              className={`text-sm ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <span className="text-red-500">*</span> = Required Information
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateProductModal;
